/**
 * File upload configuration
 */
export const FILE_UPLOAD = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10 MB
  MAX_VIDEO_SIZE: 5 * 1024 * 1024, // 5 MB
  ACCEPTED_TYPES: ".pdf,.png,.jpeg,.jpg,.doc,.docx,.mp4",
} as const;

/**
 * Pagination configuration
 */
export const PAGINATION = {
  INITIAL_PAGE: 1,
  CHATS_PER_PAGE: 20,
  MAX_PAGINATION_CALLS: 5,
} as const;

/**
 * Breakpoints for responsive design
 */
export const BREAKPOINTS = {
  MOBILE: 768,
  TABLET: 1024,
  DESKTOP: 1280,
} as const;

/**
 * Scroll behavior configuration
 */
export const SCROLL = {
  DEBOUNCE_DELAY: 100,
  SCROLL_TO_BOTTOM_DELAY: 200,
  AUTO_SCROLL_THRESHOLD: 100,
} as const;

/**
 * Search configuration
 */
export const SEARCH = {
  MIN_SEARCH_LENGTH: 2,
  DEBOUNCE_DELAY: 500,
} as const;

/**
 * UI text content
 */
export const UI_TEXT = {
  EMPTY_STATES: {
    NO_SELECTION: {
      title: "Select a conversation",
      description: "Choose a conversation from the list to start messaging",
    },
    NO_MESSAGES: {
      title: "No messages yet",
      description: "Start the conversation by sending a message below",
    },
    NO_RESULTS: {
      title: "No results found",
      description: "Try adjusting your search terms",
    },
    NO_CHATS: {
      title: "No conversations",
      description: "Start a new conversation to get started",
    },
  },
  FILTERS: {
    ALL: "All",
    UNREAD: "Unread",
  },
  BUTTONS: {
    SEND: "Send",
    SENDING: "Sending...",
    VIEW_BOOKING: "View Booking",
    VIEW_QUERY: "View Booking Query",
  },
  PLACEHOLDERS: {
    SEARCH: "Search conversations...",
    MESSAGE: "Type your message...",
  },
  ERRORS: {
    EMPTY_MESSAGE: "Please enter a message or attach a file",
    NO_CHAT_SELECTED: "No chat selected",
    SEND_FAILED: "Failed to send message",
    SEND_ERROR: "An error occurred while sending the message",
    LOAD_MESSAGES_FAILED: "Failed to load messages",
    SEARCH_FAILED: "Failed to search messages",
    SEARCH_EMPTY: "Please enter a search term",
    BOOKING_MISSING: "Booking information is missing",
    BOOKING_QUERY_MISSING: "Booking query information is missing",
    BOOKING_UPDATE_FAILED: "Failed to update booking query",
  },
  SECTIONS: {
    MESSAGES: "Messages",
    DETAILS: "Details",
    PROPERTY_DETAILS: "Property Details",
    RESERVATION_DETAILS: "Reservation Details",
    BOOKING_QUERY: "Booking Query",
    CHECK_IN: "Check-in",
    CHECK_OUT: "Check-out",
  },
} as const;

/**
 * CSS class constants for consistency
 */
export const CSS_CLASSES = {
  COLORS: {
    PRIMARY: "#F56948",
    PRIMARY_HOVER: "#E5593D",
  },
  TRANSITIONS: {
    DEFAULT: "transition-all",
    DURATION_200: "duration-200",
    DURATION_300: "duration-300",
  },
  SHADOWS: {
    SM: "shadow-sm",
    MD: "shadow-md",
    LG: "shadow-lg",
  },
  ROUNDED: {
    SM: "rounded-lg",
    MD: "rounded-xl",
    LG: "rounded-2xl",
    FULL: "rounded-full",
  },
} as const;

/**
 * Animation delays in milliseconds
 */
export const ANIMATION = {
  TOAST_DURATION: 3000,
  SCROLL_ANIMATION: 200,
  FADE_IN: 150,
  FADE_OUT: 100,
} as const;

/**
 * Z-index layers
 */
export const Z_INDEX = {
  MODAL: 50,
  DROPDOWN: 40,
  HEADER: 30,
  OVERLAY: 20,
  DEFAULT: 10,
} as const;

/**
 * File type icons mapping
 */
export const FILE_TYPE_LABELS = {
  pdf: "PDF",
  doc: "DOC",
  image: "Image",
  video: "Video",
  other: "File",
} as const;

/**
 * Date format patterns
 */
export const DATE_FORMATS = {
  DISPLAY: "MMM DD, YYYY",
  TIME: "hh:mm A",
  FULL: "MMM DD, YYYY hh:mm A",
  ISO: "YYYY-MM-DD",
} as const;

/**
 * API configuration
 */
export const API = {
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  TIMEOUT: 30000,
} as const;
